try {
  class ExpressOrderForm extends HTMLElement {
    constructor() {
      super();
      this.productList = document.querySelector('.express__product-list');
      this.foundProduct = false;
      this.discontinuedMessage =
        this.getAttribute('data-discontinued-message') ||
        'This product is discontinued';
    }

    connectedCallback() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.addEventListener('click', this.handleButtonClick);
      this.addEventListener('blur', this.handleInputBlur, true);
      document
        .getElementById('csv-file-input')
        .addEventListener('change', this.updateLabel.bind(this));
      document
        .getElementById('process-csv')
        .addEventListener('click', this.processCSV.bind(this));
      this.bindRowEvents(this.querySelector('.order-form-row'));
    }

    bindRowEvents(row) {
      // Bind events to both delete button and input fields within the row
      const deleteButton = row.querySelector('.delete-row-button');
      if (deleteButton) {
        deleteButton.addEventListener('click', (event) =>
          this.deleteRow(event)
        ); // Correctly bind the context
      }

      const inputs = row.querySelectorAll('.quick_partnumber, .quick_quantity');
      inputs.forEach((input) => {
        input.addEventListener('blur', (event) => this.handleInputBlur(event)); // Attach blur event listener to each input
      });
    }

    handleButtonClick() {
      document
        .querySelector('.send-to-cart-button')
        .addEventListener('click', this.addToBag.bind(this));
    }

    handleInputBlur(event) {
      const { target } = event;
      if (target.matches('.quick_partnumber')) {
        this.validateAndUpdateRow(target.closest('.order-form-row'));
      } else if (target.matches('.quick_quantity')) {
        this.updateProductQuantity(target);
      }
    }

    updateProductQuantity(inputElement) {
      const row = inputElement.closest('.order-form-row');
      const uniqueId = row.getAttribute('data-unique-id');
      const quantity = parseInt(inputElement.value, 10) || 0;
      if (quantity <= 0) return;
      // Update the data-quantity attribute of the corresponding list item
      let listItem = this.productList.querySelector(
        `.express__product-list-item[data-unique-id="${uniqueId}"]`
      );
      if (listItem) {
        listItem.setAttribute('data-quantity', quantity);
      }
    }

    updateLabel() {
      const input = document.querySelector('#csv-file-input');
      const label = document.querySelector('#file-upload-label');

      if (input.files.length > 0) {
        const fileName = input.files[0].name;
        label.textContent = fileName;
      } else {
        label.textContent = 'Choose a CSV file...';
      }
    }

    processCSV() {
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput.files.length === 0) {
        // No file was selected
        console.error('No file selected!');
        // Handle error: Inform the user to select a file
        return;
      }

      const file = fileInput.files[0];
      if (!(file instanceof Blob)) {
        // The file is not a Blob object
        console.error('Selected item is not a file!');
        // Handle error: Inform the user to select a file
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const data = this.parseCSV(text);
        this.populateRowsFromCSV(data);
      };
      reader.onerror = (e) => {
        // Handle file reading error
        console.error('Error reading file:', e);
        // Display error message to the user
      };
      reader.readAsText(file);
    }

    parseCSV(text) {
      return text
        .trim()
        .split('\n')
        .map((line) => {
          const [partNumber, quantityString] = line
            .split(',')
            .map((field) => field.trim());
          return { partNumber, quantity: parseInt(quantityString, 10) || 0 };
        });
    }

    async addToBag() {
      const itemsToAdd = Array.from(
        this.productList.querySelectorAll('.express__product-list-item')
      )
        .map((item) => ({
          id: item.getAttribute('data-variant'),
          quantity: parseInt(item.getAttribute('data-quantity'), 10),
        }))
        .filter(({ quantity, id }) => quantity > 0 && id);

      try {
        const res = await fetch(`${window.routes.cart.add}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd }),
        });

        if (res.ok) {
          window.location.href = '/cart';
        }
      } catch (error) {
        console.error('Error adding items to bag:', error);
      }
    }

    generateUniqueId() {
      return `row-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    updateRowAndProductList(uniqueId, productData, row) {
      let { html, quantity, sku, variant, handle } = productData;
      // Check if the list item already exists
      let listItem = this.productList.querySelector(
        `.express__product-list-item[data-unique-id="${uniqueId}"]`
      );

      // If it doesn't exist, create and append it
      if (!listItem) {
        listItem = document.createElement('li');
        listItem.className = 'express__product-list-item';
        listItem.setAttribute('data-unique-id', uniqueId);
        this.productList.appendChild(listItem);
      }

      // Update the listItem's attributes and content
      listItem.setAttribute('data-quantity', quantity);
      listItem.setAttribute('data-sku', sku);
      listItem.setAttribute('data-variant', variant);
      listItem.setAttribute('data-handle', handle);

      if (html) {
        this.foundProduct = true;
        listItem.innerHTML = html;
      } else {
        this.displayError(row, 'Product data not found');
      }

      this.addRowIfLastIsFilled();
    }

    async validateAndUpdateRow(row) {
      this.clearError(row);
      const uniqueId =
        row.getAttribute('data-unique-id') || this.generateUniqueId(); // Ensure uniqueId is always available
      row.setAttribute('data-unique-id', uniqueId); // Set uniqueId back to the row if it's new

      const skuInput = row.querySelector('.quick_partnumber');
      const quantityInput = row.querySelector('.quick_quantity');
      const sku = skuInput.value.trim();
      const quantity = parseInt(quantityInput.value, 10) || 0;

      if (!sku) {
        this.removeListItem(uniqueId);
        return;
      }

      try {
        const productData = await this.fetchProductData([{ sku, quantity }]);
        this.updateRowAndProductList(uniqueId, productData[0], row);
      } catch (error) {
        this.displayError(row, error.message);
        this.removeListItem(uniqueId);
      }
      this.addRowIfLastIsFilled();
    }

    removeListItem(uniqueId) {
      let listItem = this.productList.querySelector(
        `.express__product-list-item[data-unique-id="${uniqueId}"]`
      );
      if (listItem) {
        listItem.remove();
        return;
      }
    }

    async fetchProductData(items) {
      const promises = items.map(async ({ sku, quantity }) => {
        const cardData = await this.fetchProductCard(sku, quantity);
        if (!cardData) throw new Error(`Product with SKU ${sku} not found`);
        if (cardData.discontinued === 'true') {
          throw new Error(`${this.discontinuedMessage}`);
        }
        return cardData;
      });

      return Promise.all(promises);
    }

    deleteRow(event) {
      const row = event.target.closest('.order-form-row');
      const uniqueId = row.getAttribute('data-unique-id');
      // Also remove the corresponding product list item

      if (this.querySelectorAll('.order-form-row').length > 1) {
        row.remove();
        const listItem = this.productList.querySelector(
          `.express__product-list-item[data-unique-id="${uniqueId}"]`
        );
        if (listItem) {
          listItem.remove();
        }
      }
      this.addRowIfLastIsFilled();
    }

    populateRowsFromCSV(data) {
      data.forEach(({ partNumber, quantity }, index) => {
        const uniqueId = this.generateUniqueId();
        if (partNumber && index !== 0) {
          this.addNewRow(partNumber, quantity, index, uniqueId);
        }
      });
      this.removeBlankRows();
    }

    removeBlankRows() {
      const rows = this.querySelectorAll('.order-form-row');
      rows.forEach((row) => {
        const skuInput = row.querySelector('.quick_partnumber').value.trim();
        if (!skuInput) {
          row.remove();
        }
      });
    }

    createRowElement(partNumber, quantity, rowIndex, uniqueId) {
      // Find the initial row and clone it
      const initialRow = this.querySelector('.order-form-row');
      const newRow = initialRow.cloneNode(true);
      this.clearError(newRow);
      // Update the IDs and data attributes to reflect the new row index
      newRow.setAttribute('data-row-index', rowIndex);
      newRow.setAttribute('data-unique-id', uniqueId);

      // Update the delete button
      const deleteButton = newRow.querySelector('.delete-row-button');
      deleteButton.id = `delete-${rowIndex}`;
      deleteButton.setAttribute('data-row-index', rowIndex);

      // Update the part number input
      const partNumberInput = newRow.querySelector('.quick_partnumber');
      partNumberInput.id = `part-number-${rowIndex}`;
      partNumberInput.setAttribute('data-row-index', rowIndex);
      partNumberInput.value = partNumber; // Set the provided part number

      // Update the quantity input
      const quantityInput = newRow.querySelector('.quick_quantity');
      quantityInput.id = `quantity-${rowIndex}`;
      quantityInput.setAttribute('data-row-index', rowIndex);
      quantityInput.value = quantity; // Set the provided quantity

      return newRow;
    }

    addNewRow(partNumber, quantity, rowIndex, uid) {
      let uniqueId = uid || this.generateUniqueId();
      let newRow;
      let hasData;

      if (partNumber && quantity && rowIndex) {
        newRow = this.createRowElement(
          partNumber,
          quantity,
          rowIndex,
          uniqueId
        );
        hasData = true;
      } else {
        newRow = this.createRowElement('', '', rowIndex, uniqueId);
        hasData = false;
      }

      const rowsContainer = this.querySelector('.order-form-list');

      rowsContainer.appendChild(newRow);
      this.bindRowEvents(newRow);
      if (hasData) {
        this.validateAndUpdateRow(newRow);
      }
    }

    addRowIfLastIsFilled() {
      const lastRow = this.querySelector('.order-form-row:last-child');
      const partNumber = lastRow
        .querySelector('.quick_partnumber')
        .value.trim();

      if (partNumber && this.foundProduct && !this.existingBlankRow()) {
        this.addNewRow();
      }

      this.foundProduct = false;
    }

    existingBlankRow() {
      return Array.from(this.querySelectorAll('.order-form-row')).some(
        (row) => {
          const partNumber = row
            .querySelector('.quick_partnumber')
            .value.trim();
          return !partNumber;
        }
      );
    }

    async fetchProductCard(sku, quantity) {
      try {
        // Assuming `/search` is a valid endpoint that returns HTML containing product card information
        const response = await fetch(
          `/search?q=${sku}&page=1&types=sku&section_id=component_express`
        );
        if (!response.ok)
          throw new Error(`Network response was not ok for SKU: ${sku}`);

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const card = html.querySelector('.express-card'); // Adjust the selector based on actual markup

        // Assuming the product card HTML includes attributes for SKU, variant ID, and any other necessary data
        if (!card) throw new Error(`Product card not found for SKU: ${sku}`);
        return {
          html: card.innerHTML,
          variant: card.getAttribute('data-variant-id'),
          sku: sku,
          handle: card.getAttribute('data-handle'),
          discontinued: card.getAttribute('data-discontinued'),
          uid: this.generateUniqueId(),
          quantity,
        };
      } catch (error) {
        return null;
      }
    }

    clearError(rowElement) {
      const errorDiv = rowElement.querySelector('.error-message');
      if (errorDiv) {
        errorDiv.style.display = 'none';
        errorDiv.textContent = '';
      }
    }

    displayError(rowElement, message) {
      // Display error message in the UI
      if (!rowElement) {
        return;
      }
      const errorDiv = rowElement.querySelector('.error-message');
      if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.style.display = 'block';
      } else {
        let errorElement = document.createElement('div');
        errorElement.className = 'order-form-row--full error-message';
        errorElement.innerHTML = message;
        rowElement.append(errorElement);
      }
    }
  }

  customElements.define('express-order-form', ExpressOrderForm);
} catch (error) {}
